import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Box, Flex, Text, Button } from '@skin-seoul/design-system';
import { ReviewWriteForm } from './ReviewWriteForm';
import { getDefaultConfirmationData } from '../utils/modalUtils';
import { CompleteView } from './common/CompleteView';
import { ConfirmationDialog } from './common/ConfirmationDialog';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, BORDER_RADIUS, SPACING, Z_INDEX } from './common/constants';
import { useReviewForm } from '../hooks/useReviewForm';
import { useReviewModalState } from '../hooks/useReviewModalState';
import { useReviewSubmission } from '../hooks/useReviewSubmission';
import type { ReviewModalProps } from './common/interfaces';

const MotionBox = motion.create(Box);

export const ReviewWritePcModal = ({ isOpen, productImage, onClose, onSubmit }: ReviewModalProps) => {
  const { formState, formActions, formOptions, hasUnsavedChanges, isSubmitDisabled, resetForm } = useReviewForm({
    isOpen,
  });

  const { isComplete, showConfirmation, setIsComplete, handleClose, handleConfirmClose, handleCancelClose } =
    useReviewModalState({
      isOpen,
      onClose: () => {
        resetForm();
        onClose();
      },
      hasUnsavedChanges,
      enableConfirmation: true,
    });

  const { handleSubmit } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const confirmationData = getDefaultConfirmationData();

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <MotionBox
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={Z_INDEX.modal}
            display="flex"
            alignItems="center"
            justifyContent="center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            onClick={handleClose}
          >
            <MotionBox
              bg={COLORS.background}
              borderRadius={BORDER_RADIUS.lg}
              w="686px"
              maxW="90vw"
              maxH="90vh"
              boxShadow={`0 8px 32px ${COLORS.shadow}`}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: 'spring',
                damping: 20,
                stiffness: 300,
                duration: 0.3,
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {!isComplete && (
                <Flex align="center" justify="space-between" p={`${SPACING.xl} ${SPACING.xl} 0px ${SPACING.xl}`}>
                  <Text style={{ fontSize: FONT_SIZES.xl }} fontWeight={FONT_WEIGHTS.normal} color={COLORS.text} m="0">
                    Review this product
                  </Text>
                  <Button
                    bg="none"
                    border="none"
                    style={{ fontSize: FONT_SIZES['2xl'] }}
                    fontWeight={FONT_WEIGHTS.normal}
                    color="#333"
                    cursor="pointer"
                    onClick={handleClose}
                  >
                    ×
                  </Button>
                </Flex>
              )}
              <Box
                flex="1"
                overflowY="auto"
                p={`${SPACING.lg} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl}`}
                display="flex"
                flexDirection="column"
                gap={SPACING.xl}
              >
                {!isComplete ? (
                  <ReviewWriteForm
                    formData={formState}
                    formActions={formActions}
                    options={formOptions}
                    productImage={productImage}
                    isSubmitDisabled={isSubmitDisabled}
                    onSubmit={handleSubmit}
                    variant="pc"
                  />
                ) : (
                  <CompleteView onContinue={onClose} variant="pc" />
                )}
              </Box>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>

      <ConfirmationDialog
        isOpen={showConfirmation}
        title={confirmationData.title}
        message={confirmationData.message}
        confirmText={confirmationData.confirmText}
        cancelText={confirmationData.cancelText}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};
